import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { Pagination } from '@components'
import Button from '@components/Button'
import Divider from '@components/Divider'
import EmptyRow from '@components/EmptyRow'
import Flex from '@components/Flex'
import SearchInput from '@components/SearchInput'
import Spinner from '@components/Spinner'
import Table from '@components/Table'
import Td from '@components/Td'
import Tr from '@components/Tr'
import { PencilAltIcon, TrashIcon } from '@heroicons/react/outline'
import { disciplineApi } from '@services/DisciplineApi'
import { openAlertModal, openModal } from '@store/ducks/modal'
import { useAppDispatch } from '@store/hooks'
import { useInputBounce, useQuery } from '@utils/hooks'

import EditSubSubject from '../EditSubSubject'

const styles = {
	table: 'mt-4',
	link: 'font-medium text-primary',
	divider: 'mt-[17px] mb-[12px]',
}

export default function SubSubjectTable() {
	const { id, subjectId } = useParams<{
		id: string
		subjectId: string
	}>()
	const dispatch = useAppDispatch()
	const query = useQuery()

	const navigate = useNavigate()

	const pageQuery = Number(query.get('page')) || 1
	const searchQs = query.get('search') || ''

	const [search, setSearch] = useState(searchQs)
	const [searchQuery, setSearchQuery] = useState(searchQs)
	const [page, setPage] = useState(pageQuery)

	const { data: subjectInfo } = disciplineApi.useShowSubjectOneQuery({
		id: Number(subjectId) as number,
	})

	const { data, isLoading: isLoadingSubSubjects } =
		disciplineApi.useListSubSubjectQuery({
			matterId: subjectId as string,
			search: searchQuery,
			page,
		})

	const [deleteSubSubject] = disciplineApi.useDeleteSubSubjectMutation()

	const searchCallback = useCallback(
		(value: string) => {
			navigate(`/disciplina/${id}/assunto/${subjectId}`)
			setSearchQuery(value)
		},
		[id, navigate, subjectId]
	)

	useEffect(() => {
		setPage(pageQuery)
	}, [pageQuery])

	useInputBounce(search, searchCallback)

	const baseUrl = `/disciplina/${id}/assunto/${subjectId}/sub-assunto/`

	return (
		<>
			<div className='flex gap-6 items-end mt-6'>
				<SearchInput
					placeholder='Buscar Sub Assunto'
					onChange={(e) => setSearch(e.target.value)}
					className='flex-1'
				/>
			</div>

			{isLoadingSubSubjects ? (
				<div className='flex justify-center my-10'>
					<Spinner size='md' />
				</div>
			) : (
				<div className={styles.table}>
					<Table
						th={[
							{ label: 'ID' },
							{ label: 'Nome' },
							{ label: 'Assunto' },
							{ label: 'ações' },
						]}
						variant='mixed'
					>
						{data?.data && data?.data?.length > 0 ? (
							data?.data.map(({ id: subSubjectId, name }) => {
								return (
									<Tr key={subSubjectId}>
										<Td>{subSubjectId}</Td>
										<Td hasLink className='w-[65%]'>
											<Link to={`${baseUrl}${subSubjectId}`}>
												<span className='underline underline-offset-1'>
													{name}
												</span>
											</Link>
										</Td>
										<Td className='w-[40%] px-6'>
											<span className='underline underline-offset-1'>
												{subjectInfo?.name}
											</span>
										</Td>
										<Td>
											<Flex className='items-center gap-4'>
												<Button
													className='!px-2 !py-2'
													leftIcon={<PencilAltIcon />}
													disabled={false}
													variant='outline'
													onClick={() =>
														dispatch(
															openModal({
																name: 'editSubSubject',
																modalInfo: subSubjectId,
															})
														)
													}
												>
													<></>
												</Button>
												<Button
													className='!px-2 !py-2'
													leftIcon={<TrashIcon />}
													disabled={false}
													variant='outline'
													onClick={() => {
														const removeSubSubject = {
															title: 'Remover Sub Assunto',
															message:
																'Tem certeza que deseja remover o sub assunto?',
															buttonLabel: 'Remover Assunto',
															onClick: () =>
																deleteSubSubject({
																	id: subSubjectId,
																}),
														}

														dispatch(openAlertModal(removeSubSubject))
													}}
												>
													<></>
												</Button>
											</Flex>
										</Td>
									</Tr>
								)
							})
						) : (
							<EmptyRow
								tdAmount={4}
								message='Nenhum sub assunto foi encontrada'
							/>
						)}
					</Table>
				</div>
			)}

			<Divider className={styles.divider} />
			{data?.data && data?.data?.length > 0 && (
				<Pagination
					pageCount={data?.meta.lastPage || 0}
					maxAmount={data?.meta.total || 0}
					perPage={data?.meta.perPage || 0}
				/>
			)}

			<EditSubSubject />
		</>
	)
}
